/**
 * Sign-out cache purge (task 5.3c)
 *
 * The page-side half of dropping the API cache when a session ends. Task
 * 5.3a made authenticated course and grade data persist on disk in the
 * `canvas-api-v2` cache -- that is what makes offline work -- but nothing
 * removed it when the user signed out, so on a shared device one family's
 * data outlived their session. 5.3a's identity keying means the next user
 * cannot be *served* it, so this is data at rest rather than a cross-user
 * bleed; it is still not theirs to leave behind.
 *
 * The deletion itself happens in the worker (`purgeApiCache` in
 * `../serviceWorker/cacheRules`), because the worker is the only context
 * guaranteed to hold the cache. This module's whole job is to ask for it
 * and to *wait for the answer*.
 *
 * That wait is the point. `postMessage` to a service worker is
 * fire-and-forget: post it and navigate, and a page teardown that lands
 * first leaves the cache exactly where it was, with nothing anywhere
 * reporting a problem. So the request carries a `MessageChannel` port and
 * this function resolves only once the worker has acknowledged through it.
 * Callers `await` it before they finish signing out.
 *
 * Nothing here ever throws or hangs. A sign-out must not fail because a
 * cache could not be cleared, and must not stall behind a wedged worker --
 * hence the timeout below and the result object rather than an exception.
 */

import {
  PURGE_API_CACHE_ACK_TYPE,
  PURGE_API_CACHE_MESSAGE_TYPE,
} from '../serviceWorker/cacheRules';

export { PURGE_API_CACHE_MESSAGE_TYPE };

/**
 * How long to wait for the worker's acknowledgement before giving up.
 *
 * Deleting a cache is a fast local operation, so this is a stall guard, not
 * a budget. Signing out is worth a short pause to get right; it is not
 * worth blocking on indefinitely.
 */
export const PURGE_ACK_TIMEOUT_MS = 2000;

/**
 * @typedef {object} PurgeResult
 * @property {boolean} purged whether the worker confirmed the purge ran
 * @property {string} [reason] why it did not, when it did not
 */

/**
 * Ask the active service worker to drop the API cache, and wait for it.
 *
 * Safe to call when there is no service worker at all -- development, a
 * first load before the worker has taken control, or a browser that refused
 * the registration. In that case there is also no cache to purge, since the
 * worker is the only thing that writes one, so reporting `no-worker` is a
 * complete answer rather than a swallowed failure.
 *
 * @param {ServiceWorkerContainer} [container]
 * @param {{timeoutMs?: number}} [options]
 * @returns {Promise<PurgeResult>} never rejects
 */
export async function purgeApiCacheViaWorker(
  container = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
  { timeoutMs = PURGE_ACK_TIMEOUT_MS } = {}
) {
  // Deliberately `controller` and not `ready`: the controller is the worker
  // that owns this page's caches, and `ready` never settles when no worker
  // is registered, which would turn "no worker" into a hang.
  const worker = container && container.controller;
  if (!worker || typeof worker.postMessage !== 'function') {
    return { purged: false, reason: 'no-worker' };
  }

  if (typeof MessageChannel !== 'function') {
    // No way to be told when it finished. Ask anyway -- an unconfirmed
    // purge beats no purge -- but report it honestly as unconfirmed.
    try {
      worker.postMessage({ type: PURGE_API_CACHE_MESSAGE_TYPE });
    } catch {
      // Nothing further to try.
    }
    return { purged: false, reason: 'no-ack-channel' };
  }

  const channel = new MessageChannel();
  let timer;

  try {
    const acknowledged = new Promise((resolve) => {
      channel.port1.onmessage = (event) => {
        const data = event && event.data;
        if (data && data.type === PURGE_API_CACHE_ACK_TYPE) {
          resolve({ purged: true });
        } else {
          resolve({ purged: false, reason: 'unexpected-reply' });
        }
      };
    });

    const timedOut = new Promise((resolve) => {
      timer = setTimeout(() => resolve({ purged: false, reason: 'timeout' }), timeoutMs);
    });

    worker.postMessage({ type: PURGE_API_CACHE_MESSAGE_TYPE }, [channel.port2]);

    return await Promise.race([acknowledged, timedOut]);
  } catch (error) {
    console.error('API cache purge failed:', error);
    return { purged: false, reason: 'failed' };
  } finally {
    clearTimeout(timer);
    try {
      channel.port1.close();
    } catch {
      // Already closed, or a port implementation that does not need it.
    }
  }
}
